/**
 * Secure Device Fingerprint Utility
 * Using FingerprintJS with maximum security measures:
 * - SHA-256 hashing
 * - localStorage (persists across sessions for device recognition)
 * - Tamper detection with HMAC
 * - Device metadata for backend verification
 */
import FingerprintJS from '@fingerprintjs/fingerprintjs';

// Storage keys
const FINGERPRINT_KEY = 'dfp';  // Obfuscated key name
const CHECKSUM_KEY = 'dfc';      // Checksum for tamper detection
const TIMESTAMP_KEY = 'dft';     // Fingerprint generation time

// Secret salt for hashing (could be from env in production)
const SALT = 'bousanm-dfp-2024-secure';

// Cached fingerprint promise
let fingerprintPromise: Promise<string> | null = null;

/**
 * SHA-256 hash function using Web Crypto API
 */
const sha256 = async (message: string): Promise<string> => {
    const msgBuffer = new TextEncoder().encode(message);
    const hashBuffer = await crypto.subtle.digest('SHA-256', msgBuffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
};

/**
 * Generate HMAC-like checksum for tamper detection
 */
const generateChecksum = async (fingerprint: string): Promise<string> => {
    const data = fingerprint + SALT + navigator.userAgent;
    return (await sha256(data)).substring(0, 16);
};

/**
 * Verify fingerprint wasn't tampered with
 */
const verifyIntegrity = async (fingerprint: string, storedChecksum: string): Promise<boolean> => {
    const expectedChecksum = await generateChecksum(fingerprint);
    return expectedChecksum === storedChecksum;
};

/**
 * Get device metadata for backend verification
 */
export const getDeviceMetadata = (): {
    userAgent: string;
    language: string;
    timezone: number;
    platform: string;
    screenResolution: string;
    colorDepth: number;
    hardwareConcurrency: number;
} => ({
    userAgent: navigator.userAgent,
    language: navigator.language,
    timezone: new Date().getTimezoneOffset(),
    platform: navigator.platform || 'unknown',
    screenResolution: `${window.screen?.width || 0}x${window.screen?.height || 0}`,
    colorDepth: window.screen?.colorDepth || 0,
    hardwareConcurrency: navigator.hardwareConcurrency || 0,
});

/**
 * Initialize secure fingerprint
 */
export const initFingerprint = async (): Promise<string> => {
    try {
        // Load FingerprintJS with a timeout to prevent hangs (e.g. in Brave)
        const fpPromise = FingerprintJS.load();
        const timeoutPromise = new Promise<never>((_, reject) => 
            setTimeout(() => reject(new Error('FingerprintJS timeout')), 3000)
        );

        const fp = await Promise.race([fpPromise, timeoutPromise]) as any;
        const result = await Promise.race([fp.get(), timeoutPromise]) as any;

        // Get raw fingerprint
        const rawFingerprint = result.visitorId;

        // Hash with salt for security
        const hashedFingerprint = await sha256(rawFingerprint + SALT);

        // Generate checksum for tamper detection
        const checksum = await generateChecksum(hashedFingerprint);

        // Store in localStorage (persists across sessions for device recognition)
        localStorage.setItem(FINGERPRINT_KEY, hashedFingerprint);
        localStorage.setItem(CHECKSUM_KEY, checksum);
        localStorage.setItem(TIMESTAMP_KEY, Date.now().toString());

        return hashedFingerprint;
    } catch (error) {
        console.error('Fingerprint error:', error);
        
        // If it's a timeout and we're in Brave, don't keep trying to load the library
        // @ts-ignore
        if (navigator.brave && error instanceof Error && error.message.includes('timeout')) {
            console.warn('🛡️ Brave Shields likely blocking FingerprintJS, using fallback directly next time');
        }
        
        return generateFallbackFingerprint();
    }
};

/**
 * Get stored fingerprint with integrity check
 */
export const getStoredFingerprint = async (): Promise<string | null> => {
    const stored = localStorage.getItem(FINGERPRINT_KEY);
    const checksum = localStorage.getItem(CHECKSUM_KEY);

    if (!stored || !checksum) {
        return null;
    }

    // Verify integrity (tamper detection)
    const isValid = await verifyIntegrity(stored, checksum);

    if (!isValid) {
        console.warn('Fingerprint tamper detected! Regenerating...');
        clearDeviceFingerprint();
        return null;
    }

    return stored;
};

/**
 * Get fingerprint - sync version (for immediate use)
 * Returns cached or triggers async generation
 */
export const getDeviceFingerprint = (): string => {
    const stored = localStorage.getItem(FINGERPRINT_KEY);

    if (stored) {
        // Async integrity check in background
        getStoredFingerprint().then(valid => {
            if (!valid) {
                fingerprintPromise = initFingerprint();
            }
        });
        return stored;
    }

    // Trigger async generation
    if (!fingerprintPromise) {
        fingerprintPromise = initFingerprint();
    }

    // Return sync fallback
    return generateFallbackFingerprintSync();
};

/**
 * Get fingerprint - async version (preferred, includes integrity check)
 */
export const getDeviceFingerprintAsync = async (): Promise<string> => {
    const stored = await getStoredFingerprint();

    if (stored) {
        return stored;
    }

    if (!fingerprintPromise) {
        fingerprintPromise = initFingerprint();
    }

    try {
        return await Promise.race([
            fingerprintPromise,
            new Promise<never>((_, reject) =>
                setTimeout(() => reject(new Error('Fingerprint generation timeout')), 3000)
            ),
        ]);
    } catch {
        console.warn('⚠️ Fingerprint async timed out, using sync fallback');
        fingerprintPromise = null; // Reset stale promise
        return generateFallbackFingerprintSync();
    }
};

/**
 * Get full device info for login (fingerprint + metadata)
 */
export const getSecureDeviceInfo = async (): Promise<{
    fingerprint: string;
    metadata: ReturnType<typeof getDeviceMetadata>;
    timestamp: number;
}> => {
    const fingerprint = await getDeviceFingerprintAsync();
    return {
        fingerprint,
        metadata: getDeviceMetadata(),
        timestamp: Date.now(),
    };
};

/**
 * Async fallback fingerprint
 */
const generateFallbackFingerprint = async (): Promise<string> => {
    const data = [
        navigator.userAgent,
        navigator.language,
        navigator.hardwareConcurrency?.toString() || '',
        new Date().getTimezoneOffset().toString(),
        navigator.platform || '',
        SALT,
    ].join('|');

    return 'fb-' + (await sha256(data)).substring(0, 32);
};

/**
 * Sync fallback (no crypto)
 */
const generateFallbackFingerprintSync = (): string => {
    const data = [
        navigator.userAgent,
        navigator.language,
        navigator.hardwareConcurrency?.toString() || '',
        new Date().getTimezoneOffset().toString(),
    ].join('|');

    let hash = 0;
    for (let i = 0; i < data.length; i++) {
        const char = data.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash;
    }
    return 'fbs-' + Math.abs(hash).toString(16).padStart(8, '0');
};

/**
 * Clear all fingerprint data
 */
export const clearDeviceFingerprint = (): void => {
    localStorage.removeItem(FINGERPRINT_KEY);
    localStorage.removeItem(CHECKSUM_KEY);
    localStorage.removeItem(TIMESTAMP_KEY);
    fingerprintPromise = null;
};

/**
 * Check if fingerprint exists and is valid
 */
export const hasValidFingerprint = async (): Promise<boolean> => {
    const stored = await getStoredFingerprint();
    return stored !== null;
};

// ============ Device Token (Server-issued ID) ============
const DEVICE_TOKEN_KEY = 'bdt'; // Bousanm Device Token

/**
 * Store device token after successful verification
 * This is the server-issued device ID, more reliable than fingerprint
 */
export const storeDeviceToken = (deviceId: string, userId: string): void => {
    const tokenData = {
        deviceId,
        userId,
        storedAt: Date.now(),
    };
    localStorage.setItem(DEVICE_TOKEN_KEY, JSON.stringify(tokenData));
};

/**
 * Get stored device token for a user
 * Returns null if no token or token is for different user
 */
export const getStoredDeviceToken = (userId?: string): string | null => {
    try {
        const stored = localStorage.getItem(DEVICE_TOKEN_KEY);
        if (!stored) return null;

        const data = JSON.parse(stored);

        // If userId provided, verify it matches
        if (userId && data.userId !== userId) {
            return null;
        }

        return data.deviceId;
    } catch {
        return null;
    }
};

/**
 * Get device token without user verification (for initial login)
 */
export const getAnyStoredDeviceToken = (): string | null => {
    try {
        const stored = localStorage.getItem(DEVICE_TOKEN_KEY);
        if (!stored) return null;
        return JSON.parse(stored).deviceId;
    } catch {
        return null;
    }
};

/**
 * Clear device token (on logout or manual clear)
 */
export const clearDeviceToken = (): void => {
    localStorage.removeItem(DEVICE_TOKEN_KEY);
};

/**
 * Check if device has a valid token
 */
export const hasDeviceToken = (): boolean => {
    return getAnyStoredDeviceToken() !== null;
};

/**
 * Get complete device info for login (fingerprint + token + metadata)
 */
export const getFullDeviceInfo = async (): Promise<{
    fingerprint: string;
    deviceToken: string | null;
    metadata: ReturnType<typeof getDeviceMetadata>;
}> => {
    const fingerprint = await getDeviceFingerprintAsync();
    const deviceToken = getAnyStoredDeviceToken();
    return {
        fingerprint,
        deviceToken,
        metadata: getDeviceMetadata(),
    };
};

export default getDeviceFingerprint;
